import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import {
  GlobalSearchResultTypes,
  type GlobalSearchResultTypeEnum,
} from '../../value-objects/enums'
import { type GlobalSearchItemMetadata } from '../../value-objects/GlobalSearchItemMetadata'
import { type GlobalSearchResultItemConstructor } from '../../value-objects/GlobalSearchResultItem'
import { type GlobalSearchResultApiModel } from '../api-models/GlobalSearchResultApiModel'
import {
  GlobalSearchWarningCodes,
  type GlobalSearchApiMetadata,
} from '../types/GlobalSearchResponse'

type MappedGlobalSearchResult = {
  items: GlobalSearchResultItemConstructor[]
  counts: Map<GlobalSearchResultTypeEnum, number>
  isTruncated: boolean
  isUnsupportedQuery: boolean
}

function mapAmount(rawAmount?: {
  value: number
  currency_name: CurrencyName
}): { value: number; currencyName: CurrencyName } | undefined {
  return rawAmount
    ? { value: rawAmount.value, currencyName: rawAmount.currency_name }
    : undefined
}

function mapItemMetadata(
  item: GlobalSearchResultApiModel
): GlobalSearchItemMetadata {
  const identifiers = item.attributes.identifiers ?? {}

  switch (item.type) {
    case GlobalSearchResultTypes.BILL:
      return {
        status: item.attributes.status,
        date: item.attributes.date,
        invoiceNumber: identifiers.invoice_number ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.PAYMENT:
      return {
        status: item.attributes.status,
        date: item.attributes.date,
        vendorPaymentMethod: identifiers.vendor_payment_method ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.CATALOG_ITEM:
      return {
        sku: identifiers.sku ?? null,
        vendorName: item.attributes.vendor_name ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.VENDOR:
      return {
        isPreferredVendor: item.attributes.is_preferred,
        externalId: identifiers.vendor_external_id ?? null,
      }
    case GlobalSearchResultTypes.ORDER_REQUEST:
    case GlobalSearchResultTypes.EXPENSE_REQUEST:
    case GlobalSearchResultTypes.TRAVEL_REQUEST:
    case GlobalSearchResultTypes.PURCHASE_ORDER:
      return {
        status: item.attributes.status,
        date: item.attributes.date,
        amount: mapAmount(item.attributes.amount),
      }
  }
}

export function mapGlobalSearchResultsApiModel(
  items: GlobalSearchResultApiModel[],
  metadata?: GlobalSearchApiMetadata
): MappedGlobalSearchResult {
  const mappedItems: GlobalSearchResultItemConstructor[] = items.map((item) => {
    return {
      type: item.type,
      uuid: item.uuid,
      id: item.attributes.subject.id,
      label: item.attributes.subject?.label,
      metadata: mapItemMetadata(item),
    }
  })

  const counts = new Map<GlobalSearchResultTypeEnum, number>()
  if (metadata?.counts) {
    for (const entry of metadata.counts) {
      counts.set(entry.type, entry.count)
    }
  }

  const warnings = metadata?.warnings ?? []
  const isTruncated = warnings.some(
    (w) => w.code === GlobalSearchWarningCodes.TRUNCATED_RESULTS
  )
  const isUnsupportedQuery = warnings.some(
    (w) => w.code === GlobalSearchWarningCodes.UNSUPPORTED_QUERY
  )

  return { items: mappedItems, counts, isTruncated, isUnsupportedQuery }
}
